import React, { useLayoutEffect } from 'react';
import {
  useActionData,
  useAsyncValue,
  useNavigate,
  useSubmit,
} from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { SubmitHandler } from 'react-hook-form';

// Components Styled
import { Container } from './styles';

// Components
import NewWorkOrderForm from '../../../../components/forms/NewWorkOrderForm';

// types
import { type WorkOrder } from '../../../../types/workOrder';
import { OrderType } from '../../../../types/orders';
import { ActionDataNewAction } from './types';

// Utils
import { createFormDataFromValues } from '../../../../utils/forms';
import { decompressSessionStorage } from '../../../../utils/sessionStorage';
import { seedOfflineDraft } from './offlineDraft';

// Redux
import { roidOTSelected } from '../../../../state/actions/ui/roidOTSelectedGeneralView';

// Constants
import { formActionNaming } from '../../../../components/forms/ActionForms/constants';

// Offline
import { isActuallyOnline } from '../../../../offline/checkConnectivity';

export default function NewActionPage(): JSX.Element {
  const { t } = useTranslation('forms');
  const actionData = useActionData() as ActionDataNewAction;
  const loaderData = useAsyncValue() as WorkOrder & Partial<ActionDataNewAction>;
  const dispatch = useDispatch();
  const submit = useSubmit();

  const navigate = useNavigate();

  useLayoutEffect(() => {
    if (!loaderData || !actionData) {
      navigate('../');
    }
    if (actionData?.orderParentRoid && actionData?.orderParentId) {
      dispatch(
        roidOTSelected(
          OrderType.Action,
          actionData.orderParentRoid,
          actionData.orderParentId
        )
      );
    }
  }, []);

  const onFormSubmit: SubmitHandler<WorkOrder> = async (values) => {
    // ── OFFLINE: mark as offline and continue to wizard ──
    // Phase 2 (Animals/Action/saveData.ts) will queue the COMPLETE operation
    const online = await isActuallyOnline();
    if (!online) {
      const dataFormRaw = sessionStorage.getItem(formActionNaming.NEW_ACTION_DATA);
      if (dataFormRaw) {
        const dataForm = JSON.parse(decompressSessionStorage(dataFormRaw));

        seedOfflineDraft({
          ...dataForm,
          ...values,
          orderParentId: actionData?.orderParentId || dataForm.orderParentId,
          orderParentRoid: actionData?.orderParentRoid || dataForm.orderParentRoid,
        });

        // Continue to wizard — user fills all steps, then Phase 2 queues everything
        navigate('details/operators-and-vehicles');
      }
      return;
    }

    // ── ONLINE: normal flow (unchanged) ──
    const formData = createFormDataFromValues({
      ...values,
      intent: 'save-data',
    });
    dispatch(
      roidOTSelected(
        OrderType.Action,
        actionData.orderParentRoid,
        actionData.orderParentId
      )
    );
    submit(formData, {
      method: 'POST',
      action: '.',
    });
  };

  return (
    <Container>
      <NewWorkOrderForm
        onFormSubmit={onFormSubmit}
        initialData={{
          ...loaderData,
        }}
        namingPersistForm={formActionNaming.NEW_ACTION_DATA}
        isAction={true}
        registeredByRequired={true}
        titleBasicInfo={t('basicInfo')}
      />
    </Container>
  );
}
